import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  getAdmins, 
  createAdmin, 
  updateAdmin, 
  deleteAdmin, 
  toggleAdminStatus,
  getAvailablePermissions 
} from '../../utils/api';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { 
  FaEdit, 
  FaTrash, 
  FaToggleOn, 
  FaToggleOff, 
  FaPlus,
  FaUserShield,
  FaUser,
  FaKey,
  FaCalendarAlt,
  FaEye
} from 'react-icons/fa';
import toast from 'react-hot-toast';

const AdminManagement = () => {
  const { user: currentUser, isSuperAdmin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin',
    permissions: [],
    is_active: true
  });

  useEffect(() => {
    if (isSuperAdmin) {
      loadAdmins();
      loadPermissions();
    }
  }, [isSuperAdmin]);

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const response = await getAdmins();
      setAdmins(response.admins || []);
    } catch (error) {
      toast.error('Failed to load admins');
      console.error('Error loading admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const permissions = await getAvailablePermissions();
      setAvailablePermissions(permissions);
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAdmin) {
        await updateAdmin(editingAdmin.user.id, formData);
        toast.success('Admin updated successfully');
      } else {
        await createAdmin(formData);
        toast.success('Admin created successfully');
      }
      setShowModal(false);
      resetForm();
      loadAdmins();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save admin');
      console.error('Error saving admin:', error);
    }
  };

  const handleDelete = async (id) => {
    if (id === currentUser.id) {
      toast.error('Cannot delete yourself');
      return;
    }

    if (window.confirm('Are you sure you want to delete this admin?')) {
      try {
        await deleteAdmin(id);
        toast.success('Admin deleted successfully');
        loadAdmins();
      } catch (error) {
        toast.error('Failed to delete admin');
        console.error('Error deleting admin:', error);
      }
    }
  };

  const handleToggleStatus = async (admin) => {
    if (admin.user.id === currentUser.id) {
      toast.error('Cannot change your own status');
      return;
    }

    try {
      await toggleAdminStatus(admin.user.id);
      toast.success(`Admin ${admin.user.is_active ? 'disabled' : 'enabled'} successfully`);
      loadAdmins();
    } catch (error) {
      toast.error('Failed to toggle admin status');
      console.error('Error toggling status:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'admin',
      permissions: [],
      is_active: true
    });
    setEditingAdmin(null);
  };

  const getPermissionCount = (permissions) => {
    if (!permissions) return 0;
    if (Array.isArray(permissions)) return permissions.length;
    return 0;
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <AdminSidebar />
        <div className="flex-1 p-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
            <p className="mt-2 text-gray-600">Super admin access required to manage other admins.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <AdminSidebar />
      
      <div className="flex-1 p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Admin Management</h1>
            <p className="text-gray-600">Create and manage admin users with specific permissions</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FaPlus className="h-4 w-4 mr-2" />
            Add New Admin
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FaUserShield className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Total Admins</p>
                <p className="text-2xl font-bold">{admins.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <FaUser className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Active Admins</p>
                <p className="text-2xl font-bold">
                  {admins.filter(a => a.user.is_active).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <FaKey className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Average Permissions</p>
                <p className="text-2xl font-bold">
                  {admins.length > 0 
                    ? Math.round(admins.reduce((sum, a) => sum + getPermissionCount(a.permissions), 0) / admins.length)
                    : 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Admins Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading admins...</p>
            </div>
          ) : (
            <>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Admin
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Permissions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {admins.map((admin) => (
                    <tr key={admin.user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                              admin.user.role === 'super_admin' ? 'bg-purple-100' : 'bg-blue-100'
                            }`}>
                              <span className={`font-medium ${
                                admin.user.role === 'super_admin' ? 'text-purple-800' : 'text-blue-800'
                              }`}>
                                {admin.user.name?.charAt(0)?.toUpperCase() || 'A'}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {admin.user.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {admin.user.email}
                            </div>
                            {admin.admin_profile?.created_by && (
                              <div className="text-xs text-gray-400 mt-1">
                                Created by: {admin.admin_profile.created_by}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          admin.user.role === 'super_admin' 
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {admin.user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {admin.permissions?.slice(0, 3).map((perm, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded"
                            >
                              {perm.replace('manage_', '').replace('_', ' ')}
                            </span>
                          ))}
                          {getPermissionCount(admin.permissions) > 3 && (
                            <span className="px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded">
                              +{getPermissionCount(admin.permissions) - 3} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <FaCalendarAlt className="h-3 w-3 mr-1 text-gray-400" />
                          {new Date(admin.user.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleStatus(admin)}
                          disabled={admin.user.id === currentUser.id}
                          className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${
                            admin.user.is_active
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          } ${admin.user.id === currentUser.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={admin.user.id === currentUser.id ? 'Cannot change your own status' : ''}
                        >
                          {admin.user.is_active ? (
                            <>
                              <FaToggleOn className="h-4 w-4" />
                              <span>Active</span>
                            </>
                          ) : (
                            <>
                              <FaToggleOff className="h-4 w-4" />
                              <span>Inactive</span>
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setEditingAdmin(admin);
                              setFormData({
                                name: admin.user.name,
                                email: admin.user.email,
                                password: '',
                                role: admin.user.role,
                                permissions: admin.permissions || [],
                                is_active: admin.user.is_active
                              });
                              setShowModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Edit"
                          >
                            <FaEdit className="h-4 w-4" />
                          </button>
                          
                          {admin.user.role !== 'super_admin' && (
                            <button
                              onClick={() => handleDelete(admin.user.id)}
                              disabled={admin.user.id === currentUser.id}
                              className={`p-1 ${
                                admin.user.id === currentUser.id 
                                  ? 'text-gray-400 cursor-not-allowed' 
                                  : 'text-red-600 hover:text-red-900'
                              }`}
                              title={admin.user.id === currentUser.id ? 'Cannot delete yourself' : 'Delete'}
                            >
                              <FaTrash className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {admins.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <FaUserShield className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p>No admins found. Create your first admin user.</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingAdmin ? 'Edit Admin' : 'Add New Admin'}
                </h3>
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                        disabled={!!editingAdmin}
                      />
                    </div>
                    {!editingAdmin && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Password *
                        </label>
                        <input
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required={!editingAdmin}
                          minLength="8"
                          placeholder="Minimum 8 characters"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Role *
                      </label>
                      <select
                        value={formData.role}
                        onChange={(e) => setFormData({...formData, role: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="admin">Admin</option>
                        <option value="user">Regular User</option>
                      </select>
                    </div>
                    {formData.role === 'admin' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Permissions *
                        </label>
                        <div className="mt-2 space-y-2 max-h-40 overflow-y-auto p-2 border border-gray-200 rounded">
                          {availablePermissions.map((perm) => (
                            <div key={perm} className="flex items-center">
                              <input
                                type="checkbox"
                                id={perm}
                                checked={formData.permissions.includes(perm)}
                                onChange={(e) => {
                                  const newPermissions = e.target.checked
                                    ? [...formData.permissions, perm]
                                    : formData.permissions.filter(p => p !== perm);
                                  setFormData({...formData, permissions: newPermissions});
                                }}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label htmlFor={perm} className="ml-2 text-sm text-gray-700">
                                {perm.split('_').slice(1).join(' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </label>
                            </div>
                          ))}
                        </div>
                        {formData.permissions.length === 0 && (
                          <p className="text-red-500 text-xs mt-1">Please select at least one permission</p>
                        )}
                      </div>
                    )}
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Active</span>
                      </label>
                    </div>
                  </div>
                  <div className="mt-5 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={formData.role === 'admin' && formData.permissions.length === 0}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {editingAdmin ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminManagement;